// @ts-nocheck

/**
 * MSI3 Patient Test Service
 * 
 * Provides machine-testable interfaces for the patient subscription
 * and revenue sharing system. Enables automated testing without
 * requiring real Firestore/Stripe integrations.
 * 
 * Usage:
 * - Dev mode: Set window.__MSI3_TEST_MODE__ = true
 * - Import and use patientTestService methods
 * - Generate test data using testDataGenerators
 */

import { MSI3_CONSTANTS } from './constants';

const { REVENUE_STATUS, PRICING, REVENUE_TIERS } = MSI3_CONSTANTS;

// ============================================
// Test Mode Detection
// ============================================

export const isTestMode = () => {
  if (typeof window !== 'undefined') {
    return window.__MSI3_TEST_MODE__ === true || 
           window.location.hostname === 'localhost' ||
           window.location.hostname === '127.0.0.1' ||
           process.env.REACT_APP_MSI3_TEST_MODE === 'true';
  }
  return process.env.MSI3_TEST_MODE === 'true';
};

// ============================================
// In-Memory Test Data Store
// ============================================

const testDataStore = {
  patients: new Map(),
  enrollments: new Map(),
  facilities: new Map(),
  cities: new Map(),
  clinicalContacts: [],
  revenueTransfers: [],
  outOfMarketLogs: [],
};

// ============================================
// Test Data Generators
// ============================================

export const testDataGenerators = {
  /**
   * Generate a test patient user
   */
  generatePatient: (overrides = {}) => {
    const id = `test-patient-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    return {
      uid: id,
      id,
      email: overrides.email || `patient-${id}@test.healthdesk.co`,
      name: overrides.name || 'Test Patient',
      lastName: overrides.lastName || 'User',
      role: 'patient',
      subscriptionTier: overrides.subscriptionTier || 'free',
      tokensUsedThisMonth: overrides.tokensUsedThisMonth || 0,
      lastTokenReset: overrides.lastTokenReset || new Date(),
      createdAt: new Date(),
      updatedAt: new Date(),
      ...overrides,
    };
  },

  /**
   * Generate a test city/population center
   */
  generateCity: (overrides = {}) => {
    const id = overrides.id || `test-city-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    return {
      id,
      sequenceNumber: overrides.sequenceNumber || Math.floor(Math.random() * 100) + 1,
      cityName: overrides.cityName || 'Test City',
      displayName: overrides.displayName || overrides.cityName || 'Test City',
      adminDivision1: overrides.adminDivision1 || 'Test County',
      countryCode: overrides.countryCode || 'KE',
      countryName: overrides.countryName || 'Kenya',
      population: overrides.population || 500000,
      estimatedChronicDiseasePatients: overrides.estimatedChronicDiseasePatients || 50000,
      latitude: overrides.latitude || -1.2921,
      longitude: overrides.longitude || 36.8219,
      currency: overrides.currency || 'KES',
      languagePrimary: overrides.languagePrimary || 'English',
      languageSecondary: overrides.languageSecondary || 'Swahili',
      region: overrides.region || 'Central',
      status: overrides.status || 'active',
      cityManagerRole: overrides.cityManagerRole || `Role_CityManager_${overrides.cityName || 'TestCity'}`,
      createdAt: new Date(),
      updatedAt: new Date(),
      ...overrides,
    };
  },

  /**
   * Generate a test facility partner
   */
  generateFacility: (cityId, overrides = {}) => {
    const id = overrides.id || `test-facility-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const code = overrides.uniqueSignupCode || `fac_${Math.random().toString(36).substr(2, 8)}`;
    return {
      id,
      facilityUserId: overrides.facilityUserId || `facility-user-${id}`,
      cityId: cityId,
      facilityName: overrides.facilityName || 'Test Hospital',
      tier: overrides.tier || 2,
      bedCount: overrides.bedCount || 200,
      facilityType: overrides.facilityType || 'Private Hospital',
      uniqueSignupCode: code,
      earlyAdopterPosition: overrides.earlyAdopterPosition || null,
      earlyAdopterProtectionMonths: overrides.earlyAdopterProtectionMonths || 
        (overrides.earlyAdopterPosition ? 36 : 18),
      partnershipStatus: overrides.partnershipStatus || 'active',
      partnershipSignedDate: overrides.partnershipSignedDate || new Date(),
      paymentDelayDays: overrides.paymentDelayDays || 30,
      marketPenetrationAtPartnership: overrides.marketPenetrationAtPartnership || 0,
      contactEmail: overrides.contactEmail || `contact@${id}.test`,
      qrCodeGeneratedAt: new Date(),
      createdAt: new Date(),
      updatedAt: new Date(),
      ...overrides,
    };
  },

  /**
   * Generate a test patient enrollment
   */
  generateEnrollment: (patientUserId, facilityId, cityId, overrides = {}) => {
    const id = overrides.id || `test-enrollment-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const subscriptionTier = overrides.subscriptionTier || 'free';
    const revenueSharePct = overrides.lockedRevenueSharePercentage || 75;
    const monthlyAmount = patientTestService.calculateMonthlyRevenue(subscriptionTier, revenueSharePct);
    
    return {
      id,
      patientUserId,
      subscriptionTier,
      enrollingFacilityId: facilityId,
      enrollmentCityId: cityId,
      facilityCityId: cityId,
      cityDetectionMethod: overrides.cityDetectionMethod || 'user_selected',
      countsTowardMarketPenetration: overrides.countsTowardMarketPenetration !== false,
      outOfMarketReason: overrides.outOfMarketReason || null,
      marketPenetrationAtEnrollment: overrides.marketPenetrationAtEnrollment || 0,
      lockedRevenueSharePercentage: revenueSharePct,
      lockedMonthlyAmount: monthlyAmount,
      currentRevenueFacilityId: subscriptionTier === 'free' ? null : facilityId,
      lastClinicalContactDate: overrides.lastClinicalContactDate || new Date(),
      revenueShareStatus: subscriptionTier === 'free' ? REVENUE_STATUS.NONE : REVENUE_STATUS.ACTIVE,
      inactivityProtectionMonths: overrides.inactivityProtectionMonths || 18,
      enrollmentDate: overrides.enrollmentDate || new Date(),
      createdAt: new Date(),
      updatedAt: new Date(),
      ...overrides,
    };
  },

  /**
   * Generate a test clinical contact
   */
  generateClinicalContact: (patientUserId, facilityUserId, overrides = {}) => {
    return {
      id: overrides.id || `test-contact-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      patientUserId,
      facilityUserId,
      providerId: overrides.providerId || `provider-${Date.now()}`,
      contactDate: overrides.contactDate || new Date(),
      contactType: overrides.contactType || 'chartmind_cmr_scan',
      chartmindEncounterId: overrides.chartmindEncounterId || null,
      patientDataAccessed: overrides.patientDataAccessed || ['name', 'dob'],
      createdAt: new Date(),
      ...overrides,
    };
  },

  /**
   * Generate a complete test scenario with city, facility, and patients
   */
  generateTestScenario: (options = {}) => {
    const {
      cityOverrides = {},
      facilityOverrides = {},
      patientCount = 10,
      paidPatientRatio = 0.3,
      marketPenetration = 0.1, // 10% default
    } = options;

    // Generate city
    const city = testDataGenerators.generateCity({
      cityName: 'Nairobi',
      population: 4400000,
      estimatedChronicDiseasePatients: 440000,
      ...cityOverrides,
    });

    // Generate facility
    const facility = testDataGenerators.generateFacility(city.id, {
      facilityName: 'Test General Hospital',
      ...facilityOverrides,
    });

    // Generate patients with enrollments
    const patients = [];
    const enrollments = [];

    for (let i = 0; i < patientCount; i++) {
      const isPaid = i < Math.floor(patientCount * paidPatientRatio);
      const tier = isPaid ? ['small', 'medium', 'large'][Math.floor(Math.random() * 3)] : 'free';
      
      const patient = testDataGenerators.generatePatient({
        name: `Test Patient ${i + 1}`,
        subscriptionTier: tier,
      });

      const enrollment = testDataGenerators.generateEnrollment(
        patient.uid,
        facility.id,
        city.id,
        { subscriptionTier: tier }
      );

      patients.push(patient);
      enrollments.push(enrollment);
    }

    return {
      city,
      facility,
      patients,
      enrollments,
      summary: {
        totalPatients: patients.length,
        paidPatients: patients.filter(p => p.subscriptionTier !== 'free').length,
        freePatients: patients.filter(p => p.subscriptionTier === 'free').length,
        estimatedPenetration: (patientCount / city.estimatedChronicDiseasePatients) * 100,
      },
    };
  },
};

// ============================================
// Patient Test Service
// ============================================

export const patientTestService = {
  // Data store access
  getTestDataStore: () => testDataStore,
  
  /**
   * Clear all test data
   */
  clearTestData: () => {
    testDataStore.patients.clear();
    testDataStore.enrollments.clear();
    testDataStore.facilities.clear();
    testDataStore.cities.clear();
    testDataStore.clinicalContacts = [];
    testDataStore.revenueTransfers = [];
    testDataStore.outOfMarketLogs = [];
  },

  /**
   * Seed test data from a scenario
   */
  seedTestScenario: (scenario) => {
    // Add city
    testDataStore.cities.set(scenario.city.id, scenario.city);
    
    // Add facility
    testDataStore.facilities.set(scenario.facility.id, scenario.facility);
    
    // Add patients and enrollments
    scenario.patients.forEach(patient => {
      testDataStore.patients.set(patient.uid, patient);
    });
    
    scenario.enrollments.forEach(enrollment => {
      testDataStore.enrollments.set(enrollment.id, enrollment);
    });

    return scenario;
  },

  // ==========================================
  // Pure Functions (No Firebase Dependencies)
  // ==========================================

  /**
   * Calculate revenue share percentage based on market penetration
   * Pure function - no external dependencies
   */
  getRevenueSharePercentage: (penetration) => {
    if (penetration <= REVENUE_TIERS.LOW_PENETRATION.maxPercent) {
      return REVENUE_TIERS.LOW_PENETRATION.revenueShare; // 75%
    }
    if (penetration <= REVENUE_TIERS.MEDIUM_PENETRATION.maxPercent) {
      return REVENUE_TIERS.MEDIUM_PENETRATION.revenueShare; // 50%
    }
    return REVENUE_TIERS.HIGH_PENETRATION.revenueShare; // 25%
  },

  /**
   * Calculate monthly revenue from subscription tier and revenue share
   * Pure function - no external dependencies
   */
  calculateMonthlyRevenue: (subscriptionTier, revenueSharePct, pricingTier = 'TIER_1') => {
    const annualPricing = PRICING[pricingTier] || PRICING.TIER_1;
    const annualAmount = annualPricing[subscriptionTier] || 0;
    const monthlySubscription = annualAmount / 12;
    const monthlyRevenue = monthlySubscription * (revenueSharePct / 100);
    return parseFloat(monthlyRevenue.toFixed(4));
  },

  /**
   * Calculate market penetration from enrollments and addressable market
   * Pure function - operates on provided data
   */
  calculatePenetration: (enrollments, addressableMarket) => {
    if (!addressableMarket || addressableMarket <= 0) return 0;
    const inMarketCount = enrollments.filter(e => e.countsTowardMarketPenetration).length;
    return (inMarketCount / addressableMarket) * 100;
  },

  /**
   * Determine if enrollment is in-market
   */
  isInMarket: (enrollmentCityId, facilityCityId) => {
    return enrollmentCityId === facilityCityId;
  },

  /**
   * Check if protection period has expired
   */
  isProtectionExpired: (lastContactDate, protectionMonths) => {
    if (!lastContactDate) return true;
    const lastContact = new Date(lastContactDate);
    const now = new Date();
    const monthsSinceContact = (now - lastContact) / (1000 * 60 * 60 * 24 * 30);
    return monthsSinceContact >= protectionMonths;
  },

  /**
   * Get lookback window (90 days before protection expiration)
   */
  getLookbackWindow: (lastContactDate, protectionMonths) => {
    const lastContact = new Date(lastContactDate);
    const protectionEnd = new Date(lastContact);
    protectionEnd.setMonth(protectionEnd.getMonth() + protectionMonths);
    
    const lookbackStart = new Date(protectionEnd);
    lookbackStart.setDate(lookbackStart.getDate() - MSI3_CONSTANTS.LOOKBACK_DAYS);
    
    return {
      lookbackStart,
      protectionEnd,
    };
  },

  // ==========================================
  // Test Mode Operations (In-Memory)
  // ==========================================

  /**
   * Simulate patient enrollment (test mode)
   */
  simulateEnrollment: (enrollmentData) => {
    const {
      patientUserId,
      subscriptionTier = 'free',
      facilityCode = null,
      selectedCityId = null,
    } = enrollmentData;

    // Check existing enrollment
    const existingEnrollment = Array.from(testDataStore.enrollments.values())
      .find(e => e.patientUserId === patientUserId);
    
    if (existingEnrollment) {
      return {
        success: false,
        error: 'Patient already has an enrollment record',
        existingEnrollment,
      };
    }

    // No facility code case
    if (!facilityCode) {
      const enrollment = testDataGenerators.generateEnrollment(
        patientUserId,
        null,
        selectedCityId,
        { subscriptionTier, enrollingFacilityId: null }
      );
      testDataStore.enrollments.set(enrollment.id, enrollment);
      return {
        success: true,
        revenueShare: false,
        reason: 'no_facility_code',
        enrollment,
      };
    }

    // Find facility by code
    const facility = Array.from(testDataStore.facilities.values())
      .find(f => f.uniqueSignupCode === facilityCode && f.partnershipStatus === 'active');

    if (!facility) {
      return {
        success: false,
        error: 'Invalid or inactive facility code',
      };
    }

    // Calculate penetration
    const cityEnrollments = Array.from(testDataStore.enrollments.values())
      .filter(e => e.facilityCityId === facility.cityId);
    const city = testDataStore.cities.get(facility.cityId);
    const penetration = patientTestService.calculatePenetration(
      cityEnrollments,
      city?.estimatedChronicDiseasePatients || 50000
    );

    // Get revenue share percentage
    const revenueSharePct = patientTestService.getRevenueSharePercentage(penetration);
    const monthlyAmount = patientTestService.calculateMonthlyRevenue(subscriptionTier, revenueSharePct);

    // Create enrollment
    const enrollment = testDataGenerators.generateEnrollment(
      patientUserId,
      facility.id,
      facility.cityId,
      {
        subscriptionTier,
        marketPenetrationAtEnrollment: penetration,
        lockedRevenueSharePercentage: revenueSharePct,
        lockedMonthlyAmount: monthlyAmount,
      }
    );

    testDataStore.enrollments.set(enrollment.id, enrollment);

    return {
      success: true,
      revenueShare: subscriptionTier !== 'free',
      facilityId: facility.id,
      facilityName: facility.facilityName,
      subscriptionTier,
      revenueSharePct,
      monthlyAmount,
      penetrationAtEnrollment: penetration,
      enrollment,
    };
  },

  /**
   * Simulate subscription tier change
   */
  simulateTierChange: (patientUserId, newTier) => {
    const enrollment = Array.from(testDataStore.enrollments.values())
      .find(e => e.patientUserId === patientUserId);

    if (!enrollment) {
      return { success: false, error: 'Patient enrollment not found' };
    }

    const oldTier = enrollment.subscriptionTier;

    // Update enrollment
    const updatedEnrollment = {
      ...enrollment,
      subscriptionTier: newTier,
      updatedAt: new Date(),
    };

    // Upgrade from free to paid
    if (oldTier === 'free' && newTier !== 'free') {
      if (enrollment.enrollingFacilityId) {
        // Calculate new revenue
        const city = testDataStore.cities.get(enrollment.facilityCityId);
        const cityEnrollments = Array.from(testDataStore.enrollments.values())
          .filter(e => e.facilityCityId === enrollment.facilityCityId);
        const penetration = patientTestService.calculatePenetration(
          cityEnrollments,
          city?.estimatedChronicDiseasePatients || 50000
        );
        const revenueSharePct = patientTestService.getRevenueSharePercentage(penetration);
        const monthlyAmount = patientTestService.calculateMonthlyRevenue(newTier, revenueSharePct);

        updatedEnrollment.lockedRevenueSharePercentage = revenueSharePct;
        updatedEnrollment.lockedMonthlyAmount = monthlyAmount;
        updatedEnrollment.currentRevenueFacilityId = enrollment.enrollingFacilityId;
        updatedEnrollment.revenueShareStatus = REVENUE_STATUS.ACTIVE;
        updatedEnrollment.lastClinicalContactDate = new Date();
      }
    }
    // Downgrade from paid to free
    else if (oldTier !== 'free' && newTier === 'free') {
      updatedEnrollment.revenueShareStatus = REVENUE_STATUS.NONE;
      updatedEnrollment.currentRevenueFacilityId = null;
    }
    // Change between paid tiers
    else if (oldTier !== 'free' && newTier !== 'free') {
      updatedEnrollment.lockedMonthlyAmount = patientTestService.calculateMonthlyRevenue(
        newTier,
        enrollment.lockedRevenueSharePercentage
      );
    }

    testDataStore.enrollments.set(enrollment.id, updatedEnrollment);

    return {
      success: true,
      oldTier,
      newTier,
      enrollment: updatedEnrollment,
    };
  },

  /**
   * Simulate clinical contact
   */
  simulateClinicalContact: (patientUserId, facilityUserId, providerId) => {
    const contact = testDataGenerators.generateClinicalContact(patientUserId, facilityUserId, {
      providerId,
      contactDate: new Date(),
    });

    testDataStore.clinicalContacts.push(contact);

    // Update enrollment's last contact date
    const enrollment = Array.from(testDataStore.enrollments.values())
      .find(e => e.patientUserId === patientUserId);

    if (enrollment) {
      const updatedEnrollment = {
        ...enrollment,
        lastClinicalContactDate: new Date(),
        updatedAt: new Date(),
      };

      // If paused and contact is with original facility, restart
      if (
        enrollment.revenueShareStatus === REVENUE_STATUS.PAUSED &&
        enrollment.subscriptionTier !== 'free'
      ) {
        const facility = testDataStore.facilities.get(facilityUserId);
        if (facility?.id === enrollment.enrollingFacilityId) {
          updatedEnrollment.revenueShareStatus = REVENUE_STATUS.ACTIVE;
          updatedEnrollment.currentRevenueFacilityId = enrollment.enrollingFacilityId;
        }
      }

      testDataStore.enrollments.set(enrollment.id, updatedEnrollment);
    }

    return { success: true, contact };
  },

  /**
   * Get city metrics (test mode)
   */
  getCityMetrics: (cityId) => {
    const city = testDataStore.cities.get(cityId);
    if (!city) return null;

    const enrollments = Array.from(testDataStore.enrollments.values())
      .filter(e => e.facilityCityId === cityId || e.enrollmentCityId === cityId);

    const tierCounts = {
      free: enrollments.filter(e => e.subscriptionTier === 'free').length,
      small: enrollments.filter(e => e.subscriptionTier === 'small').length,
      medium: enrollments.filter(e => e.subscriptionTier === 'medium').length,
      large: enrollments.filter(e => e.subscriptionTier === 'large').length,
    };

    const totalEnrolled = enrollments.length;
    const paidEnrolled = tierCounts.small + tierCounts.medium + tierCounts.large;
    const penetration = patientTestService.calculatePenetration(
      enrollments,
      city.estimatedChronicDiseasePatients
    );
    const revenueShareRate = patientTestService.getRevenueSharePercentage(penetration);

    // Calculate total monthly revenue
    const totalMonthlyRevenue = enrollments
      .filter(e => e.revenueShareStatus === REVENUE_STATUS.ACTIVE)
      .reduce((sum, e) => sum + (e.lockedMonthlyAmount || 0), 0);

    return {
      city,
      enrollments: {
        total: totalEnrolled,
        paid: paidEnrolled,
        free: tierCounts.free,
        byTier: tierCounts,
      },
      penetration: {
        percentage: parseFloat(penetration.toFixed(2)),
        tier: penetration <= 33 ? '0-33%' : penetration <= 66 ? '34-66%' : '67-100%',
        revenueShareRate,
      },
      revenue: {
        totalMonthlyRevenue: parseFloat(totalMonthlyRevenue.toFixed(2)),
        activeEnrollments: enrollments.filter(e => e.revenueShareStatus === REVENUE_STATUS.ACTIVE).length,
      },
    };
  },

  /**
   * Get facility metrics (test mode)
   */
  getFacilityMetrics: (facilityId) => {
    const facility = testDataStore.facilities.get(facilityId);
    if (!facility) return null;

    const enrollments = Array.from(testDataStore.enrollments.values())
      .filter(e => e.enrollingFacilityId === facilityId);

    const activeRevenue = enrollments
      .filter(e => e.currentRevenueFacilityId === facilityId && e.revenueShareStatus === REVENUE_STATUS.ACTIVE);

    const totalMonthlyRevenue = activeRevenue
      .reduce((sum, e) => sum + (e.lockedMonthlyAmount || 0), 0);

    return {
      facility,
      enrollments: {
        total: enrollments.length,
        activeRevenue: activeRevenue.length,
        byTier: {
          free: enrollments.filter(e => e.subscriptionTier === 'free').length,
          small: enrollments.filter(e => e.subscriptionTier === 'small').length,
          medium: enrollments.filter(e => e.subscriptionTier === 'medium').length,
          large: enrollments.filter(e => e.subscriptionTier === 'large').length,
        },
      },
      revenue: {
        totalMonthlyRevenue: parseFloat(totalMonthlyRevenue.toFixed(2)),
        averageRevenueShare: activeRevenue.length > 0
          ? activeRevenue.reduce((sum, e) => sum + e.lockedRevenueSharePercentage, 0) / activeRevenue.length
          : 0,
      },
    };
  },
};

// ============================================
// Dev Mode Window API
// ============================================

if (typeof window !== 'undefined') {
  // Expose test service globally for browser console testing
  window.__MSI3_TEST_SERVICE__ = patientTestService;
  window.__MSI3_TEST_GENERATORS__ = testDataGenerators;
  
  // Enable test mode function
  window.enableMSI3TestMode = () => {
    window.__MSI3_TEST_MODE__ = true;
  };
}

export default patientTestService;

